<script lang="ts">
  /**
   * The sheet behind the profile button: the shell's `ProfileMenu` template,
   * with Interlock's rows (`docs/decisions/0054`). "You" on the tab bar is
   * what you have played; this is the rest.
   *
   * The template's order is the rule it documents: places first (My scores,
   * How to play, Settings, and, while the device offers it, Install
   * Interlock); the account's own actions second (Change password and Sign
   * out for a signed-in player, the way to sign in for a guest); and the
   * admin panel last, alone, only for an administrator.
   *
   * The parent owns `open`. A row's press closes the sheet (the template
   * does it), then reports what was picked; where that leads is
   * `App.svelte`'s.
   */
  import { ProfileMenu } from '@artibens/app-shell';
  import type { ProfileGroup, ProfileRow } from '@artibens/app-shell';

  import { account, signOut } from '../state/account.svelte.js';
  import { install } from '../state/install.svelte.js';

  const {
    open,
    onclose,
    onMyScores,
    onHowToPlay,
    onSettings,
    onInstall,
    onAdmin,
    onSignIn,
    onChangePassword,
  }: {
    open: boolean;
    onclose: () => void;
    onMyScores: () => void;
    onHowToPlay: () => void;
    onSettings: () => void;
    onInstall: () => void;
    onAdmin: () => void;
    onSignIn: () => void;
    onChangePassword: () => void;
  } = $props();

  const player = $derived(account.status === 'signed-in' ? account.player : null);
  const canSignIn = $derived(account.status === 'guest');
  const canInstall = $derived(install.availability !== 'unavailable');

  const places = $derived<ProfileRow[]>([
    {
      id: 'scores',
      icon: 'trophy',
      title: 'My scores',
      detail: 'Your personal bests, next to the records',
      testid: 'profile-my-scores',
      onclick: onMyScores,
    },
    {
      id: 'help',
      icon: 'book',
      title: 'How to play',
      detail: 'The basics of the game',
      testid: 'profile-how-to-play',
      onclick: onHowToPlay,
    },
    {
      id: 'settings',
      icon: 'gear',
      title: 'Settings',
      detail: 'Sound, colours, controls',
      testid: 'profile-settings',
      onclick: onSettings,
    },
    ...(canInstall
      ? [
          {
            id: 'install',
            icon: 'download' as const,
            title: 'Install Interlock',
            detail: 'Play from your home screen',
            testid: 'install-entry',
            onclick: onInstall,
          },
        ]
      : []),
  ]);

  const accountRows = $derived<ProfileRow[]>(
    player !== null
      ? [
          {
            id: 'password',
            icon: 'key',
            title: 'Change password',
            detail: 'Signs your other devices out',
            testid: 'profile-change-password',
            onclick: onChangePassword,
          },
          {
            id: 'sign-out',
            icon: 'exit',
            title: 'Sign out',
            detail: 'You play on as a guest',
            tone: 'danger',
            testid: 'profile-sign-out',
            onclick: () => void signOut(),
          },
        ]
      : canSignIn
        ? [
            {
              id: 'sign-in',
              icon: 'join',
              title: 'Sign in or create an account',
              detail: 'Sync your bests and post them to the leaderboard',
              testid: 'profile-sign-in',
              onclick: onSignIn,
            },
          ]
        : [],
  );

  const groups = $derived<ProfileGroup[]>([
    { id: 'places', rows: places },
    ...(accountRows.length > 0 ? [{ id: 'account', rows: accountRows }] : []),
  ]);

  const admin = $derived<ProfileRow | undefined>(
    player !== null && account.isAdmin
      ? {
          id: 'admin',
          icon: 'shield',
          title: 'Admin panel',
          detail: 'Players and their accounts',
          testid: 'open-admin',
          onclick: onAdmin,
        }
      : undefined,
  );
</script>

<ProfileMenu
  {open}
  label="Your profile"
  {onclose}
  who={{
    name: player?.username ?? 'Guest',
    state: player === null ? 'guest' : 'account',
    detail: player === null ? 'Your scores stay on this device' : 'Signed in',
    testid: 'profile-name',
  }}
  {groups}
  {admin}
  testid="profile-sheet"
/>
